// A program's insertion order as one editable cell: the filename as an Open
// link, attach/replace and remove controls, and the whole cell a drop target
// for a replacement PDF. Used in the Programs list column and the edit form —
// one component, two placements (Edward, 2026-09-09).
//
// Not an EditableCell: that edits one scalar through a text/select input, and
// a file is neither. Replace when an IO already exists asks first, so a PDF
// dropped onto the wrong row costs a click rather than a document.
import { useState } from 'react';
import axios from 'axios';
import FileDropZone, { isPdfFile } from './FileDropZone';

export { isPdfFile };

const API = process.env.REACT_APP_API_URL;

// Fetched as an authed blob (the axios interceptor attaches the Cognito JWT —
// a plain link/window.open to the API URL would 401), then opened in a new tab.
export async function openIoPdf(programId) {
  try {
    const res = await axios.get(`${API}/programs/${programId}/io-pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    alert('Could not open the IO PDF: ' + (err.response?.status === 404 ? 'no PDF is stored for this program.' : err.message));
  }
}

const CELL_COLORS = {
  accent: '#8e44ad', border: 'transparent', tint: '#f6eefc', band: 'transparent',
  text: '#2c3e50', muted: '#999', radius: '4px',
};
const iconBtn = {
  background: 'none', border: 'none', cursor: 'pointer', padding: '0 3px',
  fontSize: '13px', lineHeight: 1, color: '#555',
};
const linkBtn = {
  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
  color: '#8e44ad', textDecoration: 'underline', fontSize: 'inherit',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
};

/**
 * @param {object} props
 * @param {{id:number, io_pdf_filename?:string|null}} props.program
 * @param {boolean} props.canEdit  attach/replace/remove controls (admin only — the API enforces the same)
 * @param {(program:object)=>void} [props.onChanged]  receives the updated program row from the API
 * @param {object} [props.style]
 */
export default function IoPdfCell({ program, canEdit, onChanged, style }) {
  const [busy, setBusy] = useState('');   // '' | 'uploading' | 'removing'
  const filename = program.io_pdf_filename;

  const replace = async (file) => {
    if (!file) return;
    if (!isPdfFile(file)) {
      alert(`${file.name || 'That file'} isn't a PDF. The insertion order has to be a PDF.`);
      return;
    }
    if (filename && !window.confirm(`Replace ${filename} with ${file.name}? The current PDF will be deleted.`)) return;
    setBusy('uploading');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/programs/${program.id}/io-pdf`, fd);
      onChanged?.(res.data);
    } catch (err) {
      alert('Could not attach the IO PDF: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBusy('');
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${filename} from this program? The PDF will be deleted.`)) return;
    setBusy('removing');
    try {
      const res = await axios.delete(`${API}/programs/${program.id}/io-pdf`);
      onChanged?.(res.data);
    } catch (err) {
      alert('Could not remove the IO PDF: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBusy('');
    }
  };

  const body = (
    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
      {busy ? (
        <span style={{ color: '#999', fontSize: '12px' }}>{busy === 'uploading' ? 'Uploading…' : 'Removing…'}</span>
      ) : filename ? (
        <button type="button" style={linkBtn} title={`Open ${filename}`}
          onClick={(e) => { e.stopPropagation(); openIoPdf(program.id); }}>
          {filename}
        </button>
      ) : (
        <span style={{ color: '#ccc' }}>—</span>
      )}
      {canEdit && !busy && (
        <span style={{ display: 'inline-flex', flexShrink: 0 }}>
          {filename && (
            <button type="button" style={iconBtn} title="Remove the insertion order"
              onClick={(e) => { e.stopPropagation(); remove(); }}>✕</button>
          )}
          <span style={{ ...iconBtn, color: '#8e44ad' }} title={filename ? 'Replace the insertion order (or drop a PDF here)' : 'Attach the insertion order (or drop a PDF here)'}>📎</span>
        </span>
      )}
    </span>
  );

  if (!canEdit) return <span style={style}>{body}</span>;
  return (
    <FileDropZone
      accept="application/pdf" multiple={false} disabled={!!busy}
      colors={CELL_COLORS}
      ariaLabel={filename ? 'Replace the insertion order' : 'Attach the insertion order'}
      style={{ padding: '2px 4px', textAlign: 'left', border: '2px dashed transparent', ...(style || {}) }}
      onFiles={(files) => replace(files[0])}
    >
      {body}
    </FileDropZone>
  );
}
